class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.trigger = document.querySelector('.buy-buttons-block');
    this.realForm = document.querySelector('form[data-type="add-to-cart-form"]');
    // Two submit buttons share this attribute: the desktop pill's and the
    // mobile bar's. Both need the real form wired up, whichever is visible.
    this.submitButtons = this.querySelectorAll('[data-atcbar-submit]');

    this.swatch = this.querySelector('.atcbar__swatch');
    this.swatchTrigger = this.querySelector('.atcbar__swatch-trigger');
    if (this.swatch && this.swatchTrigger && !this.swatchTrigger.disabled) {
      this.swatchTrigger.addEventListener('click', () => {
        const isOpen = this.swatch.classList.toggle('active');
        this.swatchTrigger.setAttribute('aria-expanded', String(isOpen));
      });

      // Close the dropdown on any click outside the sticky bar itself.
      this.onDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.onDocumentClick);
    }

    // Mirror the PDP's own delivery-estimate text (blocks/buy-buttons.liquid's
    // `.delivery-estimate` paragraph, sourced from that block's
    // `delivery_estimate_text` setting) instead of duplicating the string
    // here — that setting lives on a block this snippet can't read directly.
    // Copying the already-rendered text keeps the two in sync automatically
    // whenever a merchant edits the setting, with nothing to update by hand.
    const deliveryEl = document.querySelector('.buy-buttons-block .delivery-estimate');
    const deliveryText = this.querySelector('[data-atcbar-delivery]');
    if (deliveryEl && deliveryText) {
      deliveryText.textContent = deliveryEl.textContent.trim();
      deliveryText.hidden = false;
    }

    // Mirror the real PDP add-to-cart button's already-correct per-color
    // styling onto BOTH sticky submit buttons (desktop pill + mobile bar),
    // instead of maintaining a second color table here. blocks/add-to-cart.liquid's
    // "match_variant_color" feature computes three custom properties
    // (--add-to-cart-bg-color, --add-to-cart-bg-image, --add-to-cart-text-color)
    // and for patterned colorways like Ombre/Tortoise it correctly uses the
    // product's real photo as --add-to-cart-bg-image rather than a flat hex
    // — this snippet's own Liquid-side color_map (still used as the
    // pre-JS/no-JS fallback via atcbar_accent) can't reproduce that, which
    // is exactly why those two colors looked off before. Reading the
    // already-resolved values here guarantees both buttons always match the
    // real PDP button, including for colors added later, with nothing to
    // keep in sync. Looping this.submitButtons (not querying the mobile
    // button specifically) is what makes this apply to the desktop pill too.
    const realAddToCart = document.querySelector('.buy-buttons-block .add-to-cart-button');
    if (realAddToCart) {
      const realStyle = getComputedStyle(realAddToCart);
      const bgColor = realStyle.getPropertyValue('--add-to-cart-bg-color').trim();
      const bgImage = realStyle.getPropertyValue('--add-to-cart-bg-image').trim();
      const textColor = realStyle.getPropertyValue('--add-to-cart-text-color').trim();
      this.submitButtons.forEach((button) => {
        if (bgColor) button.style.setProperty('--atcbar-accent', bgColor);
        if (bgImage && bgImage !== 'none') button.style.setProperty('--atcbar-accent-image', bgImage);
        if (textColor) button.style.setProperty('--atcbar-text-color', textColor);
      });
    }

    if (!this.trigger || !this.realForm || this.submitButtons.length === 0) return;

    // Route these buttons' submit at the real product form so the theme's
    // existing add-to-cart handling (AJAX, cart drawer, errors) runs as-is.
    // NOTE: use getAttribute, not `.id` — the form has a hidden input
    // named "id" (the variant id field), which shadows the form element's
    // own `.id` property in the DOM.
    let formId = this.realForm.getAttribute('id');
    if (!formId) {
      formId = 'StickyAddToCart-ProductForm';
      this.realForm.setAttribute('id', formId);
    }
    this.submitButtons.forEach((button) => button.setAttribute('form', formId));

    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocumentClick);
  }

  onDocumentClick(event) {
    if (!this.swatch.classList.contains('active')) return;
    if (this.contains(event.target)) return;
    this.swatch.classList.remove('active');
    this.swatchTrigger.setAttribute('aria-expanded', 'false');
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.ticking = false;
    const rect = this.trigger.getBoundingClientRect();
    const shouldBeActive = rect.bottom < 0;

    // Reset the color dropdown to its closed resting state whenever the
    // bar itself goes away (scrolled back up to the main product info
    // section), so scrolling back down later always shows it collapsed
    // rather than remembering it was left open.
    if (!shouldBeActive && this.classList.contains('active') && this.swatch) {
      this.swatch.classList.remove('active');
      if (this.swatchTrigger) this.swatchTrigger.setAttribute('aria-expanded', 'false');
    }

    this.classList.toggle('active', shouldBeActive);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
